const _ = require("lodash");
const NumberUtil = require("./Numbers");
const ErrorUtil = require("./ErrorUtil");
const ShapeUtil = require("./ShapeUtil");
const WordUtil = require("./WordUtil");

const getMinIndex = function(array) {
	return array.indexOf(_.min(array));
};

const getMaxIndex = function(array) {
	return array.indexOf(_.max(array));
};

const gcd = function(x, y) {
	if (isNaN(x) || isNaN(y)) return 0;

	return x % y === 0 ? y : gcd(y, x % y);
};

const lcm = function(x, y) {
	return (x * y) / gcd(x, y);
};

const fracHelper = function(num, den, isSimple) {
	if (isSimple) return num + "/" + den;

	return "\\frac{" + num + "}{" + den + "}";
};

const calcProduct = function(elems) {
	let product = 1;
	_.each(elems, x => (product = x * product));
	return product;
};

const evalString = function(x) {
	return eval(x);
};

const generalFrac = function(value, isSimple) {
	if (!isNaN(value)) return value;
	if (!Array.isArray(value)) return value;
	if (value.length === 3)
		return fmt(value[0]) + " " + fracHelper(value[1], value[2], isSimple);

	return fracHelper(value[0], value[1], isSimple);
};

const fmt = function(x, noRounding = false) {
	if (Array.isArray(x) && x.length > 0 && !isNaN(x[0])) return generalFrac(x);
	if (isNaN(x) || x === "") return x;

	const num = noRounding ? x : Math.round(x * 100) / 100;
	return NumberUtil.withCommas(num);
};

const fmtSide = (side, op, noRounding) => {
	return "$" + _.map(side, x => fmt(x, noRounding)).join(" " + op + " ") + "$";
};

const genArray = function(a, b, invert) {
	return invert ? [b, a] : [a, b];
};

const mult = function(a, b, invert) {
	return invert ? fmt(b) + "*" + fmt(a) : fmt(a) + "*" + fmt(b);
};

const plusOrdered = function(a, b) {
	let op = "+";
	if (_.isString(b) && _.startsWith(b, "-")) op = "";
	if (a === "") op = "";
	if (!isNaN(b) && b < 0) {
		op = "-";
		b = -1 * b;
	}
	return fmt(a) + op + fmt(b);
};

const plus = function(a, b, invert) {
	return invert ? plusOrdered(b, a) : plusOrdered(a, b);
};

const plusAll = function(elems) {
	return _.reduce(
		elems,
		(sum, elem) => {
			return plus(sum, elem);
		},
		""
	);
};

const align = function(eqText) {
	return "\\begin{align}\n" + eqText + "\\end{align}\n";
};

const diagram = function(text) {
	return "\\begin{diagram}\n" + text + "\\end{diagram}\n";
};

const preview = function(text) {
	return "\\begin{preview}\n" + text + "\\end{preview}\n";
};

const table = function(headings, alignments, rows) {
	let tableText = "";
	tableText += "\\begin{tabular}{" + alignments.join(" | ") + "}\n";
	tableText += headings.join("&") + " \\\\\n";
	tableText += "\\hline";
	tableText += rows.map(x => x.join("&") + " \\\\\n").join("");
	tableText += "\\end{tabular}";

	return tableText;
};

const boxedMult = function(a, b) {
	return "$" + fmt(a) + "*" + fmt(b) + " = \\boxed{" + fmt(a * b) + "}$";
};

const toMixed = function(value) {
	if (value.length === 3) return value;
	const rem = value[0] % value[1];
	return [(value[0] - rem) / value[1], rem, value[1]];
};

const toImproper = function(value) {
	if (!isNaN(value)) return [value, 1];
	if (value.length === 2) return value;
	return [value[0] * value[2] + value[1], value[2]];
};

const simplifyFraction = function(value) {
	const y = gcd(value[0], value[1]);
	return value.map(x => x / y);
};

const commonDenominator = function(fractions) {
	fractions = fractions.map(x => toImproper(x));
	const common = lcm(fractions[0][1], fractions[1][1]);

	return fractions.map(x => [(x[0] * common) / x[1], common]);
};

const evalGeneral = function(value) {
	if (!isNaN(value)) return value;
	if (value.length === 2) return value[0] / value[1];
	return value[0] + value[1] / value[2];
};

// maybe don't want to rely on signed zeros, but it can be convenient sometimes (like in block mountains gen)
const isNegativeZero = function(x) {
	return x === 0 && 1 / x < 0;
};

const hasPosSign = function(x) {
	return x > 0 || (x === 0 && !isNegativeZero(x));
};

const repAdd = function(times, elem, boxed) {
	let ans = times * elem;
	ans = boxed ? "\\boxed{" + ans + "}" : ans + "";

	return (
		_.range(times)
			.map(x => elem + "")
			.join("+") +
		"=" +
		ans
	);
};

const sumElements = function(elements, signs) {
	const ops = signs.map(x => (x ? "+" : "-"));
	if (signs[0]) ops[0] = "";

	return elements.map((x, i) => ops[i] + fmt(x)).join("");
};

const sumNumbers = function(numbers) {
	return sumElements(
		numbers.map(x => Math.abs(x)),
		numbers.map(x => hasPosSign(x))
	);
};

const basicSumEq = function(elems, sum) {
	return elems.join(" + ") + " = " + sum;
};

const formatEquationLines = function(eqLines, shouldAvoidBox, forceAlign) {
	return fmtEqLines(eqLines, {shouldAvoidBox, forceAlign});
};

const fmtEqLines = function(
	eqLines,
	{shouldAvoidBox, forceAlign, singleLine, forcePeriod, extraNewline} = {}
) {
	eqLines = _.uniq(eqLines);

	if (forcePeriod) {
		const i = eqLines.length - 1;
		eqLines[i] = eqLines[i] + ".";
	} else if (!shouldAvoidBox) {
		const i = eqLines.length - 1;
		eqLines[i] = "\\boxed{" + eqLines[i] + "}.";
	}

	let eqText;

	if (singleLine) {
		eqText = "$$" + eqLines.join(" = ") + "$$";
	} else if (eqLines.length <= 2 && !forceAlign) {
		eqText = "$" + eqLines.join(" = ") + "$";
		if (extraNewline) eqText += "\n";
	} else {
		const first = eqLines.shift();
		eqText = first + " &= " + eqLines.join(" \\\\\n&=");
		eqText = align(eqText);
	}

	return eqText;
};

const sqDecomp = function(x) {
	return "(" + mult(x - 1, x - 1) + ") + " + plus(x - 1, x);
};

const knownSq = function(x, boxed) {
	if (boxed) {
		return mult(x, x) + "= \\boxed{" + fmt(x * x) + "}";
	}
	return mult(x, x) + "=" + fmt(x * x);
};

const fiveSquareLines = function(x) {
	const a = x - 5;
	const b = x + 5;

	const eqLines = [
		mult(x, x),
		"(" + mult(a, b) + ") + 25",
		fmt(a * b) + " + 25",
		fmt(x * x),
	];

	return eqLines;
};

const neighboringSquareLines = function(a, isNegative) {
	const b = a + (isNegative ? -1 : 1);
	const op = isNegative ? "-" : "+";

	const eqLines = [
		mult(b, b),
		"(" + mult(a, a) + ") " + op + " " + fmt(a) + " " + op + " " + fmt(b),
		fmt(a * a) + " " + op + " " + fmt(a + b),
		fmt(b * b),
	];

	return eqLines;
};

const formatNSLines = function(a, isPositive) {
	return formatEquationLines(neighboringSquareLines(a, !isPositive));
};

const arrayDirection = function(a, b, invert) {
	return invert ? [b, a] : [a, b];
};

const specialBreakdown = function(x) {
	if (x === 99) {
		return [100, -1];
	}

	return getPositiveBreakdown(x);
};

const isSimpleBreakdown = function(breakdown) {
	const notSimple = _.filter(breakdown, x => {
		x = Math.abs(x);

		let curr = x;

		while (curr > 0) {
			if (curr % 10 > 1) return true;
			curr = Math.floor(curr / 10);
		}

		return false;
	});

	return notSimple.length === 0;
};

const isSimplePair = function(elems) {
	elems = elems.map(x => {
		while (x % 10 === 0 && x > 0) {
			x = x / 10;
		}

		return x;
	});

	const isInTimesTable = _.filter(elems, x => x > 12).length === 0;
	const hasOne = _.includes(elems, 1);

	return isInTimesTable || hasOne;
};

const getBasicFactorExprLines = function(factor, breakdown, breakdownRight) {
	const ops = breakdown.map(x => (hasPosSign(x) ? "+" : "-"));
	if (hasPosSign(breakdown[0])) ops[0] = "";

	const applyOps = arr => arr.map((x, i) => ops[i] + arr[i]);
	const multFactor = x => mult(factor, x, !breakdownRight);
	const abs = breakdown.map(x => Math.abs(x));

	const sum = _.sum(breakdown);

	const exprLines = [];
	exprLines.push(applyOps(abs.map(x => multFactor(x))).join(""));
	exprLines.push(multFactor("(" + applyOps(abs).join("") + ")"));
	exprLines.push(multFactor(sum));

	return exprLines;
};

const twoNumberProductLinesDirection = function(elems, breakdownRight) {
	const factor = elems[breakdownRight ? 0 : 1];
	const breakdown = specialBreakdown(elems[breakdownRight ? 1 : 0]);
	const eqLines = [];

	if (breakdown.length > 1) {
		const specialLines = getBasicFactorExprLines(
			factor,
			breakdown,
			breakdownRight
		);
		eqLines.push(specialLines[1]);
		eqLines.push(specialLines[0]);
		eqLines.push(sumNumbers(breakdown.map(x => factor * x)));
	}

	eqLines.push(fmt(elems[0] * elems[1]));

	return eqLines;
};

const twoNumberProductLines = function(elems) {
	let eqLines = [];

	if (elems[0] === elems[1] && elems[0] % 10 === 1 && elems[0] > 20) {
		eqLines = neighboringSquareLines(elems[0] - 1);
		eqLines.splice(0, 1);
		eqLines[0] =
			eqLines[0] + "\\quad[3]\\text{(from Neighboring Squares, Ch 5)}";
	} else if (elems[0] === elems[1] && elems[0] % 5 === 0 && elems[0] > 10) {
		eqLines = fiveSquareLines(elems[0]);
		eqLines.splice(0, 1);
		eqLines[0] =
			eqLines[0] + "\\quad[3]\\text{(from Numbers Ending in 5, Ch 5)}";
	} else if (Math.abs(elems[0] - elems[1]) === 2 && _.min(elems) > 10) {
		eqLines = multCloseLines(elems[0], elems[1]);
		eqLines.splice(0, 1);
		eqLines[0] =
			eqLines[0] + "\\quad[3]\\text{(from Multiplying Close Numbers, Ch 5)}";
	} else if (isSimplePair(elems)) {
		eqLines.push(fmt(elems[0] * elems[1]));
	} else {
		let breakdownL = specialBreakdown(elems[0]);
		let breakdownR = specialBreakdown(elems[1]);

		if (isSimpleBreakdown(breakdownL)) {
			breakdownR = [elems[1]];
		} else if (isSimpleBreakdown(breakdownR)) {
			breakdownL = [elems[0]];
		}

		if (breakdownL.length > 1 && breakdownR.length > 1) {
			ErrorUtil.log(
				"E_GENERATOR_BREAKDOWN_ERROR",
				"Can't handle multiple breakdowns: " + elems.join(",")
			);
		}

		eqLines = twoNumberProductLinesDirection(
			elems,
			breakdownR.length >= breakdownL.length
		);
	}

	return eqLines;
};

const parseZippedSum = function(expr) {
	expr = replaceAll(expr, " ", "");
	expr = replaceAll(expr, "-", "+-");
	const terms = _.split(expr, "+");
	const zipped = terms.map(t => _.split(t, "*").map(x => parseInt(x, 10)));
	return zipped;
};

const sumZipped = function(zipped) {
	const signs = zipped.map(z => hasPosSign(z[0]) === hasPosSign(z[1]));
	const terms = zipped.map(z => mult(Math.abs(z[0]), Math.abs(z[1])));
	return sumElements(terms, signs);
};

const constructAdvancedFactorLines = function(
	zipped,
	factors,
	groupRemainingBefore
) {
	const reordered = [];
	let remaining = zipped;

	_.each(factors, f => {
		const nextRemaining = [];
		const reorderedGroup = [];
		_.each(remaining, z => {
			(_.includes(z, f) ? reorderedGroup : nextRemaining).push(z);
		});
		reordered.push(reorderedGroup);
		remaining = nextRemaining;
	});

	const remLeft = remaining.map(r => r[0]);

	const basicLineGroups = [];

	_.each(factors, (f, i) => {
		const group = reordered[i];
		const breakdown = group.map(x => _.difference(x, [f])[0]);
		const bdSum = _.sum(breakdown);

		const commonIndexes = _.uniq(
			group.map(elems => _.findIndex(elems, x => x === f))
		);
		let breakdownRight = true;
		if (commonIndexes.length === 1) {
			breakdownRight = commonIndexes[0] === 0;
		} else if (_.includes(remLeft, bdSum)) {
			breakdownRight = false;
		}

		const basicLines = getBasicFactorExprLines(f, breakdown, breakdownRight);
		basicLineGroups.push(basicLines);
	});

	let basicLines = _.zip(...basicLineGroups).map(x => x.join("+"));

	if (remaining.length > 0) {
		const remainingSum = sumZipped(remaining);
		basicLines = basicLines.map(x =>
			plus(x, remainingSum, groupRemainingBefore)
		);
	}

	let advancedLines = [];
	advancedLines = advancedLines.concat(basicLines);

	if (!_.isEqual(zipped, parseZippedSum(advancedLines[0]))) {
		advancedLines = [sumZipped(zipped)].concat(advancedLines);
	}

	return advancedLines;
};

const zippedFactorLines = function(zipped, forceSimple) {
	const factor = _.intersection(...zipped)[0];
	const breakdown = _.difference(_.flatten(zipped), [factor]);

	const commonIndexes = _.uniq(
		zipped.map(elems => _.findIndex(elems, x => x === factor))
	);
	const breakdownRight = commonIndexes.length > 1 || commonIndexes[0] === 0;

	let eqLines = constructFactorExprLines(
		factor,
		breakdown,
		breakdownRight,
		forceSimple
	);

	if (commonIndexes.length > 1) {
		eqLines = [sumZipped(zipped)].concat(eqLines);
	}

	return eqLines;
};

const constructFactorExprLines = function(
	factor,
	breakdown,
	breakdownRight,
	forceSimple
) {
	let exprLines = getBasicFactorExprLines(factor, breakdown, breakdownRight);
	if (forceSimple) {
		exprLines.push(fmt(factor * _.sum(breakdown)));
	} else {
		const finishingLines = twoNumberProductLines(
			arrayDirection(factor, _.sum(breakdown), !breakdownRight)
		);
		exprLines = exprLines.concat(finishingLines);
	}

	return exprLines;
};

const genFactorExpr = function(factor, signedBreakdown, breakdownRight) {
	const factorExprLines = constructFactorExprLines(
		factor,
		signedBreakdown,
		breakdownRight
	);

	return formatEquationLines(factorExprLines);
};

const getPositiveBreakdown = function(value, forceByDigit) {
	let breakdown = [];
	let remainder = 0;

	if (value % 100 === 25 && !forceByDigit) {
		value = value - 25;
		remainder = 25;
	}

	let curr = value;
	let tens = 1;

	while (curr > 0) {
		breakdown.splice(0, 0, tens * (curr % 10));

		tens *= 10;
		curr = Math.floor(curr / 10);
	}

	breakdown.push(remainder);

	breakdown = _.filter(breakdown, x => x !== 0);

	return breakdown;
};

const getBreakdown = function(value, useAddition, forceByDigit) {
	let breakdown;

	if (useAddition) {
		breakdown = getPositiveBreakdown(value, forceByDigit);
	} else {
		const tenCeil = Math.ceil(value / 10) * 10;
		breakdown = [tenCeil, tenCeil - value];
	}

	return breakdown;
};

const genDistribute = function(
	a,
	b,
	useAddition,
	breakdownRight,
	forceByDigit
) {
	const factor = breakdownRight ? a : b;
	const multiplier = breakdownRight ? b : a;

	const operator = useAddition ? " + " : " - ";
	const breakdown = getBreakdown(multiplier, useAddition, forceByDigit);

	const multFactor = x => mult(factor, x, !breakdownRight);

	const eqLines = [
		mult(a, b),
		multFactor("(" + breakdown.join(operator) + ")"),
		breakdown.map(multFactor).join(operator),
		breakdown.map(x => factor * x).join(operator),
		fmt(factor * multiplier),
	];

	return formatEquationLines(eqLines);
};

const multCloseLines = function(a, b) {
	const avg = (a + b) / 2;

	const eqLines = [
		mult(a, b),
		"(" + mult(avg, avg) + ") - 1",
		fmt(avg * avg) + " - 1",
		fmt(avg * avg - 1),
	];

	if (avg % 10 === 5) {
		eqLines.splice(2, 0, "(" + mult(avg - 5, avg + 5) + " + 25) - 1");
	}

	return eqLines;
};

const getSortOrder = function(array) {
	const indexes = _.range(array.length);
	const elems = _.map(indexes, i => {
		return {
			index: i,
			value: array[i],
		};
	});

	const sorted = _.sortBy(elems, x => x.value);
	return _.map(sorted, x => x.index);
};

const removeVariables = function(expression) {
	return expression.replace(/[A-Za-z]/g, "");
};

const getVariables = function(expression, allowRepeats) {
	let vars = expression.replace(/[^A-Za-z]/g, "");
	vars = _.map(vars, x => x);

	return allowRepeats ? vars : _.uniq(vars);
};

const getAllVariables = function(expressions) {
	let variables = [];

	_.each(expressions, expr => {
		variables = variables.concat(getVariables(expr));
	});

	variables = _.uniq(variables);

	return variables;
};

const getIsolationOperations = function(expr, variable) {
	expr = replaceAll(expr, "-", "+-");
	const args = expr.split("+");

	const freeArgs = _.filter(args, x => !_.includes(x, variable));
	const varArgs = _.filter(args, x => _.includes(x, variable));

	const varCoeffs = varArgs.map(x => {
		const coeffStr = replaceAll(x, variable, "");
		let coeff;

		if (coeffStr === "") {
			coeff = 1;
		} else if (coeffStr === "-") {
			coeff = -1;
		} else {
			coeff = parseInt(coeffStr, 10);
		}

		return coeff;
	});

	const freeSum = _.sum(freeArgs.map(x => parseInt(x, 10)));
	const coeffSum = _.sum(varCoeffs);

	const isolationOperations = [];

	if (freeSum !== 0) {
		if (freeSum > 0) {
			isolationOperations.push(["-", freeSum]);
		} else {
			isolationOperations.push(["+", -1 * freeSum]);
		}
	}

	if (coeffSum === -1) {
		isolationOperations.push(["*", -1]);
	} else if (coeffSum !== 1) {
		isolationOperations.push(["/", coeffSum]);
	}

	return isolationOperations;
};

const isParenEnclosed = function(expr) {
	return expr.length > 0 && expr[0] === "(" && expr[expr.length - 1] === ")";
};

const getSumTerms = function(expr) {
	let terms = [];

	if (!expr) return terms;

	let term = "";
	let parenCount = 0;

	_.each(expr, (ch, i) => {
		if (ch === "(") {
			parenCount++;
		} else if (ch === ")") {
			parenCount--;
		} else if (parenCount === 0) {
			if (ch === "+") {
				terms.push(term);
				term = "";
				return;
			} else if (ch === "-") {
				terms.push(term);
				term = "-";
				return;
			}
		}

		term += ch;
	});

	terms.push(term);
	terms = _.filter(terms);

	if (terms.length === 1 && isParenEnclosed(expr)) {
		expr = expr.substring(1, expr.length - 1);
		terms = getSumTerms(expr);
	}

	return terms;
};

const flipSubtraction = function(expr) {
	const sides = expr.split("-");
	return sides[1] + "-" + sides[0];
};

const joinSum = function(terms, useNegativeParens, skipLeadingNegativeParens) {
	terms = _.filter(terms);

	let expr;

	if (useNegativeParens) {
		terms = _.map(terms, (x, i) =>
			(i > 0 || !skipLeadingNegativeParens) && _.startsWith(x, "-")
				? `(${x})`
				: x
		);

		expr = terms.join("+");
	} else {
		expr = replaceAll(terms.join("+"), "+-", "-");
	}

	return expr;
};

const getEasySimplifyingSteps = function(originalExpr) {
	let steps = [originalExpr];

	let terms = getSumTerms(originalExpr);

	const varTerms = [];
	const freeTerms = [];

	terms = terms.map(x => {
		if (getVariables(x).length === 0) {
			const evaled = eval(x);
			freeTerms.push(evaled);
			return evaled + "";
		} else {
			varTerms.push(x);
			return x;
		}
	});

	const termSumStep = joinSum(terms);
	const freeSum = _.sum(freeTerms) + "";
	let finalTerms;

	const freeSumTerms = freeSum === "0" ? [] : [freeSum];

	// to help preserve order
	if (getVariables(terms[0]).length === 0) {
		finalTerms = freeSumTerms.concat(varTerms);
	} else {
		finalTerms = varTerms.concat(freeSumTerms);
	}

	if (finalTerms.length === 0) finalTerms = ["0"];

	steps.push(termSumStep);
	steps.push(joinSum(finalTerms));

	steps = _.uniq(steps);

	return steps;
};

const extendSteps = function(stepsA, stepsB) {
	const stepLength = Math.max(stepsA.length, stepsB.length);
	while (stepsA.length < stepLength) stepsA.push(stepsA[stepsA.length - 1]);
	while (stepsB.length < stepLength) stepsB.push(stepsB[stepsB.length - 1]);
};

const getSimplifyingSteps = function(expr) {
	if (_.includes(expr, "=")) {
		const sides = expr.split("=");
		const lhsSteps = getSimplifyingSteps(sides[0]);
		const rhsSteps = getSimplifyingSteps(sides[1]);

		extendSteps(lhsSteps, rhsSteps);

		const allSteps = _.zip(lhsSteps, rhsSteps).map(x => x.join("="));

		return allSteps;
	}
	if (!_.includes(expr, "(")) return getEasySimplifyingSteps(expr);

	const regex = /\(([^()]+)\)/g;
	const firstResult = regex.exec(expr);
	const subExpr = firstResult[0];
	const prefix = expr.substring(0, firstResult.index);
	const suffix = expr.substring(firstResult.index + subExpr.length);

	let suffixOp = "";
	let suffixSteps = [""];

	if (suffix.length > 0) {
		suffixOp = suffix.substring(0, 1);
		suffixSteps = getSimplifyingSteps(suffix.substring(1));
	}

	let mainSteps = getSimplifyingSteps(subExpr.substring(1, subExpr.length - 1));
	mainSteps = mainSteps.map((x, i) =>
		i === mainSteps.length - 1 ? x : "(" + x + ")"
	);

	extendSteps(mainSteps, suffixSteps);

	let allSteps = _.zip(mainSteps, suffixSteps);
	allSteps = allSteps.map(x => prefix + x.join(suffixOp));

	const finalSteps = getEasySimplifyingSteps(allSteps[allSteps.length - 1]);
	finalSteps.splice(0, 1);

	allSteps = allSteps.concat(finalSteps);
	allSteps = _.uniq(allSteps);

	return allSteps;
};

const applyOperations = function(operations, value) {
	_.each(operations, op => {
		const type = op[0];
		const arg = op[1];

		if (type === "+") {
			value = value + arg;
		} else if (type === "-") {
			value = value - arg;
		} else if (type === "*") {
			value = value * arg;
		} else if (type === "/") {
			value = value / arg;
		}
	});

	return value;
};

const solveEquation = function(equation, variable) {
	let sides = equation.split("=");
	sides = arrayDirection(sides[0], sides[1], getVariables(sides[1]).length > 0);

	const operations = getIsolationOperations(sides[0], variable);
	const number = eval(sides[1]);

	return applyOperations(operations, number);
};

const simplifyExpr = function(expr) {
	const steps = getSimplifyingSteps(expr);

	return steps[steps.length - 1];
};

const prepForOp = function(op) {
	if (op === "+") {
		return "to";
	} else if (op === "-") {
		return "from";
	}

	return "";
};

const verbForOp = function(op, isIng) {
	if (op === "+") {
		return isIng ? "adding" : "add";
	} else if (op === "-") {
		return isIng ? "subtracting" : "subtract";
	}

	return "";
};

const opResultWordMap = {
	"+": "sum",
	"*": "product",
	"-": "difference",
	"/": "quotient",
};

const opToResultWord = function(op) {
	return opResultWordMap[op];
};

const explanationForOp = function(op, arg, isIng) {
	const expl = verbForOp(op, isIng) + " " + arg + " " + prepForOp(op);

	return expl;
};

const explanationForSolution = function(
	solutionInfo,
	shouldCapitalize,
	useIng
) {
	let expl;

	if (solutionInfo.isSimple) {
		expl = solutionInfo.explanation;
	} else {
		const ans = solutionInfo.ans;

		expl =
			explanationForOp(solutionInfo.op, solutionInfo.arg, useIng) +
			" both sides";
		expl += (useIng ? ", we" : " to") + " get $" + solutionInfo.middleEq;
		expl += solutionInfo.eqInverted
			? "$, which gives $" + solutionInfo.variable + "=" + ans + "$"
			: "=" + ans + "$";
	}

	if (shouldCapitalize) expl = WordUtil.capitalize(expl);

	return expl;
};

const getOp = function(expr) {
	const opRegex = /[-+*/]/g;
	const result = opRegex.exec(expr);
	const op = result ? result[0] : "";

	return op;
};

const verboseSimplify = function(expr) {
	if (_.includes(expr, "=")) {
		const simpExpr = simplifyExpr(expr);
		let explanation = "";

		if (simpExpr !== expr) {
			const freeSide = _.find(
				expr.split("="),
				x => getVariables(x).length === 0
			);
			const op = getOp(freeSide);

			if (op === "-") {
				const elems = freeSide.split(op);
				explanation =
					"we " + verbForOp(op) + " " + elems[1] + " from " + elems[0];
			} else {
				explanation = "we " + verbForOp(op) + " $" + freeSide + "$";
			}

			explanation += ", simplifying our equation to $" + simpExpr + "$. ";
		}

		return {
			explanation: explanation,
			ans: simpExpr,
		};
	}

	const op = getOp(expr);
	const elems = expr.split(op);

	return {
		explanation: explanationForOp(op, elems[0], true) + " " + elems[1],
		ans: eval(expr),
	};
};

const countOccurrences = function(str, find) {
	return (str.match(new RegExp(find, "g")) || []).length;
};

const replaceAll = function(str, find, replace) {
	return str.split(find).join(replace);
};

const substitute = function(expr, map) {
	_.each(map, (value, key) => {
		expr = replaceAll(expr, key, value);
	});

	return expr;
};

const variableEquations = function(varDefs) {
	const varEqs = [];

	_.each(varDefs, (value, x) => {
		varEqs.push("$" + x + "=" + value + "$");
	});

	return varEqs;
};

const replaceThenFlatten = function(expr, find, replace) {
	expr = expr.replace(find, "@");
	expr = expr.replace(/[0-9a-z+-]+\|/g, "_|");
	expr = expr.replace("@", replace);

	return expr;
};

const removeVariable = function(expr, variable, removePlaceholders) {
	const placeholder = removePlaceholders ? "" : "_|_|";

	expr = replaceAll(expr, "+|" + variable + "|", placeholder);
	expr = replaceAll(expr, "-|" + variable + "|", placeholder);
	expr = replaceAll(expr, "|" + variable + "|", placeholder);

	expr = replaceAll(expr, "+" + variable, "");
	expr = replaceAll(expr, "-" + variable, "");
	expr = replaceAll(expr, variable, "");

	return expr;
};

const joinPipes = function(expr, joinStr) {
	joinStr = joinStr || "";
	expr = replaceAll(expr, "_", "");
	return expr.split("|").join(joinStr);
};

const tabularMath = function(equations) {
	const tfmt = (sides, useEq, forceSign) => {
		const elemArrays = sides.map(x => {
			x = joinPipes(x, " & ");
			x = replaceAll(x, "-", "\\minus");
			return x;
		});

		const eqStr = useEq ? " = " : " ";

		return elemArrays[0] + " &" + eqStr + "& " + elemArrays[1] + " \\\\\n";
	};

	const lines = equations.map((x, i) => tfmt(x, i !== 1, i === 1));

	let numColumns = lines.map(x => x.split("&").length);
	numColumns = _.uniq(numColumns);

	let text = "";

	if (numColumns.length === 1) {
		text += "\\begin{tabularmath}{" + _.repeat("r", numColumns) + "}\n";
		text += lines[0];
		text += lines[1];
		text += "\\hline\n";
		text += lines[2];
		text += "\\end{tabularmath}\n";
	} else {
		text += "ERROR DISPLAYING EQUATION";
	}

	return text;
};

const createSolveCells = function(expr) {
	const sumTerms = getSumTerms(expr);
	const varCells = [];

	_.each(sumTerms, (x, i) => {
		if (i === 0) {
			varCells.push(x);
		} else if (_.startsWith(x, "-")) {
			varCells.push("-");
			varCells.push(x.substring(1));
		} else {
			varCells.push("+");
			varCells.push(x);
		}
	});

	return varCells;
};

const verboseSolve = function(equation, options = {}) {
	equation = replaceAll(equation, " ", "");
	const variable = getVariables(equation)[0];
	const sides = equation.split("=");
	const eqInverted = getVariables(sides[0]).length === 0;
	const orderedSides = arrayDirection(sides[0], sides[1], eqInverted);
	const varSide = orderedSides[0];
	const freeSide = orderedSides[1];

	if (varSide === variable) {
		const simpObj = verboseSimplify(freeSide);
		simpObj.isSimple = true;
		simpObj.variable = variable;
		return simpObj;
	}

	let exprCells = createSolveCells(varSide);
	const freeArg = -1 * evalString(removeVariable(varSide, variable));
	const absArg = Math.abs(freeArg);
	const op = freeArg > 0 ? "+" : "-";
	const ans = eval(freeSide) + freeArg;

	const opTerm = op + absArg;

	let step1aTerms = _.map(exprCells, x => "");
	const constIndex = _.findIndex(
		exprCells,
		x => !_.includes(["+", "-"], x) && getVariables(x).length === 0
	);

	if (constIndex === 0) {
		step1aTerms[constIndex] = opTerm;
	} else {
		step1aTerms[constIndex - 1] = op;
		step1aTerms[constIndex] = absArg;
	}

	let step2aTerms = _.map(exprCells, x =>
		getVariables(x).length > 0 ? x : ""
	);

	if (options.lessSpace) {
		if (constIndex === exprCells.length - 1) {
			exprCells = [exprCells.join("")];
			step1aTerms = [step1aTerms.join("")];
			step2aTerms = [step2aTerms.join("")];
		}
	}

	const step0a = exprCells.join("|");
	const step1a = step1aTerms.join("|");
	const step1b = opTerm;
	const step2a = step2aTerms.join("|");

	let arrayPairs = [[step0a, freeSide]];
	arrayPairs.push([step1a, step1b]);
	arrayPairs.push([step2a, ans + ""]);

	arrayPairs = arrayPairs.map(x => arrayDirection(x[0], x[1], eqInverted));

	const text = tabularMath(arrayPairs);

	const middleEq = arrayDirection(
		variable,
		freeSide + op + absArg,
		eqInverted
	).join("=");

	return {
		text: text,
		freeSide: freeSide,
		arg: absArg,
		variable: variable,
		op: op,
		ans: ans,
		middleEq: middleEq,
		eqInverted: eqInverted,
	};
};

const verboseRepeatVariableSolve = function(equation) {
	const v = getVariables(equation)[0];
	const sides = equation.split("=");
	const terms = sides.map(x => getSumTerms(x));
	let freeTerms = terms.map(x =>
		_.filter(x, y => getVariables(y).length === 0)
	);
	freeTerms = _.flatten(freeTerms);
	let explanation = "";

	let simpEq = equation;
	let needsCaps = true;
	let answer;

	if (freeTerms.length === 1) {
		answer = eval(freeTerms[0]);
	} else if (freeTerms.length > 1) {
		const freeValues = freeTerms.map(x => parseInt(x, 10));
		const minValue = _.min(freeValues);
		const otherValue = _.max(freeValues);

		if (minValue < 0) {
			explanation = explanationForOp("+", -1 * minValue, true);
		} else {
			explanation = explanationForOp("-", minValue, true);
		}

		explanation = WordUtil.capitalize(explanation);

		answer = otherValue - minValue;

		const newTerms = _.map(terms, sideTerms => {
			let t = [].concat(sideTerms);
			t = _.filter(t, x => x !== minValue + "");
			t = _.map(t, x => (x === otherValue + "" ? answer + "" : x));
			return t;
		});

		simpEq = _.map(newTerms, x => joinSum(x)).join("=");

		explanation += " both sides gives $$" + simpEq + ".$$\nFinally, ";
		needsCaps = false;
	}

	const prepareForSplit = function(x) {
		x = "|" + x + "|";
		x = replaceAll(x, "-", "|-|");
		x = replaceAll(x, "+", "|+|");

		x = "+" + x;
		x = replaceAll(x, "+|-", "-");

		return x;
	};

	const simpSides = simpEq.split("=").map(x => prepareForSplit(x));
	const shouldAddVar = _.includes(simpSides.join("="), "-|" + v);

	const targetInverse = arrayDirection(
		"+|" + v + "|",
		"-|" + v + "|",
		shouldAddVar
	);
	const target = targetInverse[0];
	const inverse = targetInverse[1];
	const placeholder = "_|_|";

	const steps = simpSides.map(side => {
		let suffixes = ["", ""];
		if (!_.includes(side, target)) suffixes = [placeholder, inverse];
		return [
			side + suffixes[0],
			replaceThenFlatten(side, target, inverse) + suffixes[1],
			side.replace(target, placeholder) + suffixes[1],
		];
	});

	const trimSign = x => {
		if (_.startsWith(x, "+|")) x = x.substring(1, x.length);
		return replaceAll(x, "_|+", "_|_");
	};

	let arrayPairs = _.zip(...steps);
	arrayPairs = _.map(arrayPairs, (x, i) =>
		_.includes([0, 2], i) ? _.map(x, trimSign) : x
	);

	simpEq = arrayPairs[2].map(x => joinPipes(x, false)).join("=");
	const numVariables = countOccurrences(simpEq, v);
	answer = answer / numVariables;

	explanation += WordUtil.capitalize(
		"we " +
			explanationForOp(shouldAddVar ? "+" : "-", "$" + v + "$", false) +
			" both sides to get\n",
		!needsCaps
	);
	explanation += tabularMath(arrayPairs);

	if (numVariables > 1) {
		explanation +=
			"Since $" +
			replaceAll(simpEq, v, answer) +
			"$, we have $" +
			v +
			"=" +
			answer +
			"$.\n\n";
	}

	return {
		variable: v,
		explanation: explanation,
		simpEq: simpEq,
		ans: answer,
	};
};

const divisionInfo = function(dividend, divisor, quotient) {
	return {
		dividend: dividend,
		divisor: divisor,
		remainder: dividend - divisor * quotient,
		equation: divisor + "*" + quotient + "=" + divisor * quotient,
	};
};

const getNumberLineMarks = function(marks, size, color, isBold) {
	const boldStr = isBold ? "[bold]" : "";
	if (!size) size = "medium";
	if (!color) color = "black";
	const modifiers = ["[top]", "[bottom]"];
	if (marks.length === 1) marks = [marks[0], marks[0]];
	marks = marks
		.map(
			(x, i) =>
				"\\marks" +
				modifiers[i] +
				"[" +
				size +
				"]" +
				boldStr +
				"[color=" +
				color +
				"]{1/" +
				x +
				"}"
		)
		.join(" ");

	return marks;
};

const wrapNumberLine = function(text) {
	return "\\begin{numberline}{0;1}\n" + text + "\\end{numberline}\n";
};

const standardNumberLine = function(marks, core) {
	let text = "";
	text += "\\marks[large]{1} \\modlabel{}{}{1}\n";
	text += getNumberLineMarks(marks) + "\n";
	if (core) text += core + "\n";

	return wrapNumberLine(text);
};

const arrowToFraction = function(num, den) {
	return standardNumberLine(
		[den],
		"\\fracblank[bigarrow]{" + num + "/" + den + "}{2}"
	);
};

const labelArrowToFraction = function(givenFrac) {
	let text = "";
	const color = "highlightorange";
	const simpFrac = simplifyFraction(givenFrac);

	text += "\\marks[large][bold][color=" + color + "]{1} \\modlabel{}{}{1}\n";
	text += getNumberLineMarks([simpFrac[1]], "medium", color, true) + "\n";
	text += getNumberLineMarks([givenFrac[1]], "small") + "\n";
	text += "\\label[bigarrow]{" + simpFrac.join("/") + "}{2}";

	return wrapNumberLine(text);
};

const manyArrowsToFraction = function(num, den) {
	let text = "";
	text += `\\arrowSeq{0}{${num}}{1}{${den}}`;
	text += "\\label[bold][bigarrow]{" + num + "/" + den + "}\n";

	return standardNumberLine([den], text);
};

const shadeToFraction = function(num, den) {
	const fraction = num + "/" + den;
	return standardNumberLine(
		[den],
		"\\label[bigarrow]{" + fraction + "}\n\\shade{0}{" + fraction + "}\n"
	);
};

const roundToMultiple = function(num, multiple) {
	if (isNaN(num)) num = evalGeneral(num);
	const up = num + multiple / 2;
	return Math.floor(up / multiple) * multiple;
};

const round = function(num, place) {
	if (isNaN(num)) num = evalGeneral(num);
	const tens = Math.pow(10, place);
	return roundToMultiple(num, tens);
};

const getMultipleForRounding = function(x, isSimple) {
	if (isNaN(x)) x = evalGeneral(x);
	const xStr = Math.floor(x) + "";
	let place = xStr.length - 1;
	if (!isSimple && (_.startsWith(xStr, "11") || _.startsWith(xStr, "12")))
		place -= 1;
	return Math.pow(10, place);
};

const roundDefault = function(x, isSimple) {
	if (isNaN(x)) x = evalGeneral(x);
	return roundToMultiple(x, getMultipleForRounding(x, isSimple));
};

const roundDivision = function(division) {
	const roundedDivisor = roundDefault(division[1]);
	// const estimate = roundDefault(division[0] / division[1]);
	const estimate = roundDefault(division[0] / roundedDivisor);

	return [estimate * roundedDivisor, roundedDivisor];
};

const getBoundingMultiples = function(value, multiple) {
	value = evalGeneral(value);
	return [
		Math.floor(value / multiple) * multiple,
		Math.ceil(value / multiple) * multiple,
	];
};

const getEstimationChoiceInfo = function(choices, actual) {
	const diffs = choices.map(x => Math.abs(x - actual));
	const minDiff = _.min(diffs);
	const minIndex = diffs.indexOf(minDiff);
	const ans = choices[minIndex];

	return {
		choiceAnswers: [diffs.map(x => (x === minDiff ? 1 : 0))],
		fmtChoices: choices.map(x => fmt(x)),
		ans: ans,
	};
};

const getMode = function(array) {
	return _.chain(array)
		.countBy()
		.toPairs()
		.maxBy(_.last)
		.head()
		.value();
};

const evalGeneralOp = function(terms, op) {
	if (!terms) return 0;

	let evaled;

	if (!isNaN(terms[0]) && op !== "/") {
		evaled = eval(terms.join(op));
	} else {
		const common = commonDenominator(terms);

		if (op === "+" || op === "-") {
			evaled = [eval(common[0][0] + op + common[1][0]), common[0][1]];
		} else if (op === "*") {
			evaled = [common[0][0] * common[1][0], common[0][1] * common[1][1]];
		} else if (op === "/") {
			evaled = [common[0][0] * common[1][1], common[0][1] * common[1][0]];
		}

		evaled = simplifyFraction(evaled);
		if (terms[0].length !== 2) {
			evaled = toMixed(evaled);
			if (evaled[1] === 0) evaled = evaled[0];
		}
	}

	return evaled;
};

const getEstimateInfo = function(terms, op, {ignoreBold = false} = {}) {
	let close;
	const actual = evalGeneralOp(terms, op);

	if (op === "/") {
		close = roundDivision(terms);
	} else if (op === "+" || op === "-") {
		let multiple;

		if (isNaN(terms[0])) {
			multiple = 1;
		} else {
			multiple = _.max(terms.map(x => getMultipleForRounding(x, true))) / 10;
		}

		close = terms.map((x, i) => roundToMultiple(x, multiple));
	} else if (op === "*") {
		close = terms.map(x => roundDefault(x));
	}

	const expr2 = close.join(op);

	const estimate = eval(expr2);
	const fmtExpr = terms.map(x => fmt(x)).join(op);
	const fmtExpr2 = close.map(x => fmt(x)).join(op);

	const applyBold = ignoreBold ? x => x : x => "\\bold{" + x + "}";

	const equation =
		fmtExpr + " \\approx " + fmtExpr2 + "=" + applyBold(fmt(estimate));

	const zipped = _.zip(terms, close);

	const explanationA =
		"Since $" +
		zipped.map(x => fmt(x[0]) + "$ is close to $" + fmt(x[1])).join("$ and $") +
		"$, we estimate that $" +
		fmtExpr +
		"$ is about $" +
		fmtExpr2 +
		"=" +
		applyBold(fmt(estimate)) +
		".$";

	const actualPlaces = (actual + "").length - 2;

	let delta;

	if (op === "+") {
		const evaledTerms = _.map(terms, x => _.round(evalGeneral(x)));
		const minLength = _.min(_.map(evaledTerms, x => (x + "").length));
		delta = 2 * Math.pow(10, minLength - 1);
	} else {
		delta = Math.pow(10, actualPlaces);
		delta *= Math.floor(actual / (10 * delta));
	}

	if (op === "-") {
		delta = _.max([delta, 2 * Math.pow(10, actualPlaces)]);
	}

	const roundedActual = round(actual, (delta + "").length - 1);

	let range = [roundedActual - delta, roundedActual + delta];
	range = [_.min([estimate, range[0]]), _.max([estimate, range[1]])];

	const explanationB =
		"The actual value of $" +
		fmtExpr +
		"$ is $" +
		fmt(actual) +
		"$. So, any estimate \\boxed{from " +
		_.map(range, x => fmt(x)).join(" to ") +
		"} is reasonable.";

	const isGreater = evalGeneral(actual) > estimate;

	return {
		isGreater,
		actual,
		range,
		fmtExpr,
		estimate,
		close,
		equation,
		explanationA,
		explanationB,
	};
};

const getDuplicateEstimateProblemInfo = function(expressions, op, opExprWord) {
	const values = expressions.map(x => eval(x));
	const mode = getMode(values);
	const equalBits = values.map(x => (x + "" === mode ? 1 : 0));
	const closestIndexes = _.filter(
		equalBits.map((x, i) => x * (i + 1) - 1),
		x => x !== -1
	);

	const fmtExpressions = _.map(expressions, x =>
		_.map(x.split(op), y => fmt(y)).join(op)
	);
	const closest = closestIndexes.map(x => fmtExpressions[x]);

	const modelChoices = expressions.map(x => {
		const terms = x.split(op).map(y => eval(y));
		return "$" + terms.map(y => fmt(y)).join(op) + "$";
	});

	const modelExtension = {
		choiceAnswers: [equalBits],
		choices: modelChoices,
		chooseExactly: 2,
	};

	const problemText =
		"Choose the two expressions below that have the same " + opExprWord + ".";

	let solutionText = "";
	solutionText +=
		"We estimate each " +
		opExprWord +
		", then match the two closest estimates:\n\n";
	solutionText += expressions
		.map(x => {
			const estimateInfo = getEstimateInfo(
				x.split(op).map(y => eval(y)),
				op
			);
			return "$" + estimateInfo.equation + ".$\n";
		})
		.join("");
	solutionText +=
		"\nOur estimates of $" +
		closest.join("$ and $") +
		"$ are the closest. The estimates of the other two " +
		WordUtil.pluralize(opExprWord, 2) +
		" are not even close. So, $\\boxed{" +
		closest.join("}$ and $\\boxed{") +
		"}$ are the two expressions with the same " +
		opExprWord +
		".\n\n";
	solutionText +=
		"In fact, $" +
		closest.join("=") +
		"=" +
		fmt(values[closestIndexes[0]]) +
		"$.";

	return {
		problemText: problemText,
		solutionText: solutionText,
		modelExtension: modelExtension,
	};
};

const toSetSeed = function(problemSeeds) {
	const setSeed = {};

	_.each(problemSeeds, (ps, i) => {
		setSeed[i] = ps;
	});

	return setSeed;
};

const preprocessSubstitutions = function(map) {
	const toAdd = {};

	if (map.shapeTemplate) {
		const shapeTemplate = map.shapeTemplate;

		toAdd.shape = ShapeUtil.getNameForTemplate(shapeTemplate);
		if (shapeTemplate.numPieces) toAdd.numPieces = shapeTemplate.numPieces;
	}

	if (map.numerator && map.denominator) {
		const numerator = map.numerator;
		const denominator = map.denominator;
		const fraction = [numerator, denominator];

		toAdd.fraction = fraction;
		toAdd.gcd = gcd(...fraction);
		toAdd.simpFrac = simplifyFraction(fraction);
		toAdd.simpMixed = toMixed(toAdd.simpFrac);
		toAdd.fracPart = [numerator % denominator, denominator];
		toAdd.simpFracPart = simplifyFraction(toAdd.fracPart);
	}

	_.each(toAdd, (value, key) => {
		if (!map[key]) map[key] = value;
	});
};

const addNameSubstitutions = function(person, suffix, substitutions) {
	const name = person[0];
	const isFemale = person[1] === "f";

	substitutions["name" + suffix] = name;
	substitutions["posspro" + suffix] = isFemale ? "her" : "his";
	substitutions["pronoun" + suffix] = isFemale ? "she" : "he";
	substitutions["possname" + suffix] = WordUtil.toPossessive(name);
};

const addArraySubstitutions = function(array, prefix, substitutions) {
	_.each(array, (x, i) => {
		substitutions[prefix + i] = array[i];
	});
};

const avoidUsedPairsPredicate = function(pairs, value) {
	const usedPairs = _.filter(pairs, p => _.includes(p, value));
	let usedPairValues = [];

	_.each(usedPairs, p => {
		usedPairValues = usedPairValues.concat(_.filter(p, x => x !== value));
	});

	return x => !_.includes(usedPairValues, x);
};

const filterMany = function(candidates, predicates) {
	let filtered = candidates;
	let best = candidates;

	_.each(predicates, predicate => {
		filtered = _.filter(filtered, predicate);
		if (filtered.length > 0) best = filtered;
	});

	return best;
};

const permuteTwoAvoidPairs = function(
	candidates,
	usedPairs,
	basePredicates,
	randomGen
) {
	const allPredicates = [].concat(basePredicates);
	let filtered = filterMany(candidates, allPredicates);
	const value1 = randomGen.sample(filtered);

	allPredicates.push(x => x !== value1);
	allPredicates.push(avoidUsedPairsPredicate(usedPairs, value1));
	filtered = filterMany(candidates, allPredicates);
	const value2 = randomGen.sample(filtered);

	return [value1, value2];
};

const getAvoidancePredicates = function(recentValues) {
	const predicates = [];
	_.each(recentValues, value => {
		predicates.push(x => value !== x);
	});
	return predicates;
};

const sampleEquivSums = function(randomGen, ranges, isSubtraction) {
	ranges = randomGen.permuteAll(ranges);

	const problemWithAnswer = function(ans, range) {
		range = _.map(range, x => _.round(x * ans));

		if (isSubtraction) {
			const x1 = randomGen.inRange(...range);
			return [x1 + ans, x1];
		}

		const min = _.max([100, range[0]]);
		const max = _.min([ans - 100, range[1]]);
		const a = randomGen.inRange(_.round(min), _.round(max));
		return randomGen.permuteAll([a, ans - a]);
	};

	const lastDigit = randomGen.inRange(0, 10);
	let answers = _.map(ranges, r => {
		const ans = randomGen.inRange(...r);
		return Math.floor(ans / 10) * 10 + lastDigit;
	});
	answers = randomGen.permuteAll(answers);
	answers.push(answers[0]);

	const breakdowns = _.map(answers, (sum, i) => {
		let range = [0, 1];

		// We don't want the two equivalent pairs to be too similar
		if (isSubtraction) {
			if (i === 0) range = [0.05, 0.2];
			if (i === 3) range = [0.7, 1];
		} else {
			if (i === 0) range = [0.05, 0.2];
			if (i === 3) range = [0.3, 0.5];
		}

		return problemWithAnswer(sum, range);
	});

	return breakdowns;
};

const sampleEquivProducts = function(randomGen, isDivision) {
	const allPrimes = [2, 3, 5, 7, 11, 13, 17, 19];

	const reducePF = function(pf) {
		let product = 1;

		_.each(pf, (num, i) => {
			product *= Math.pow(allPrimes[i], num);
		});

		return product;
	};

	const makeProductPair = function(pf1, pf2) {
		const p1 = reducePF(pf1);
		const p2 = reducePF(pf2);

		return [p1, _.round(p2 / p1)];
	};

	const makeQuotientPair = function(pf1, pf2) {
		const p1 = reducePF(pf1);
		const p2 = reducePF(pf2);

		return [p1, _.round(p1 / p2)];
	};

	const addPrimes = function(pf, numPrimesA, numPrimesB) {
		const newPF = [].concat(pf);
		_.each(_.range(numPrimesA), x => newPF[randomGen.inRange(0, 3)]++);
		_.each(_.range(numPrimesB), x => newPF[randomGen.inRange(4, 8)]++);
		return newPF;
	};

	const initialPF = _.map(allPrimes, x => 0);
	initialPF[0] = 1;

	let target;
	const allProducts = [];

	if (isDivision) {
		const primes1 = addPrimes(initialPF, 1, 1);
		const primes2 = addPrimes(primes1, 1, 1);
		const primes3 = addPrimes(primes2, 1, 1);
		target = reducePF(primes1);

		allProducts.push(makeQuotientPair(primes3, primes1));
		allProducts.push(makeQuotientPair(primes2, primes1));
	} else {
		const primes1 = addPrimes(initialPF, 1, 1);
		const primes2 = addPrimes(primes1, 0, 1);
		const primes3 = addPrimes(primes2, 2, 0);
		target = reducePF(primes3);

		allProducts.push(makeProductPair(primes1, primes3));
		allProducts.push(makeProductPair(primes2, primes3));
	}

	const makeIncorrectAnswer = function(tens) {
		const p1 = randomGen.inRange(10, 100);

		let elems;

		if (isDivision) {
			let p2 = target * tens * p1;
			p2 = Math.floor(p2 / p1) * p1;
			elems = [p2, p1];
		} else {
			const lastDigit2s = filterMany(_.range(0, 10), [
				x => (p1 * x) % 10 === target % 10,
			]);
			const lastDigit2 = randomGen.sample(lastDigit2s);

			let p2 = (target * tens) / p1;
			p2 = Math.floor(p2 / 10) * 10 + lastDigit2;
			elems = randomGen.permuteAll([p1, p2]);
		}

		return elems;
	};

	const incCandidates = isDivision ? [0.3, 30] : [0.1, 100];

	allProducts.push(makeIncorrectAnswer(10));
	allProducts.push(makeIncorrectAnswer(randomGen.sample(incCandidates)));

	return allProducts;
};

const getShapeTemplateForFraction = function(
	fraction,
	recentHistory,
	randomGen
) {
	const numPieces = fraction[1];

	const candidateTemplates = ShapeUtil.getTemplatesWithNumPieces(numPieces);
	const shapeTypes = _.uniq(_.map(candidateTemplates, x => x.shapeType));

	const recentTypes = _.map(recentHistory.slice(0, 3), x => x[0]);
	const predicates = getAvoidancePredicates(recentTypes);
	predicates.push(
		x => _.findIndex(recentHistory, y => _.isEqual([x, numPieces], y)) < 0
	);
	const filtered = filterMany(shapeTypes, predicates);
	const chosenType = randomGen.sample(filtered);

	const templatesWithType = _.filter(
		candidateTemplates,
		x => x.shapeType === chosenType
	);
	const chosenTemplate = randomGen.sample(templatesWithType);

	const shapeTemplate = _.extend(
		{
			shadeStart: randomGen.inRange(0, numPieces),
			numShaded: fraction[0],
		},
		chosenTemplate
	);

	return shapeTemplate;
};

const getFactorDetails = function(f) {
	const ones = f % 10;
	const tens = (f - ones) % 100;
	const hundreds = f - tens - ones;
	const onesDigit = ones;
	const tensDigit = tens / 10;
	const hundredsDigit = hundreds / 100;

	let splitValues = [];
	let digits = [];
	if (hundreds) {
		splitValues = [ones, tens, hundreds];
		digits = [onesDigit, tensDigit, hundredsDigit];
	} else if (tens) {
		splitValues = [ones, tens];
		digits = [onesDigit, tensDigit];
	} else if (ones) {
		splitValues = [ones];
		digits = [onesDigit];
	}
	const splitSum = splitValues
		.slice()
		.reverse()
		.join("+");

	const placeValueNames = [];
	if (hundreds) placeValueNames.push("hundreds");
	if (tens) placeValueNames.push("tens");
	if (ones) placeValueNames.push("ones");

	const retval = {
		f,
		ones,
		tens,
		hundreds,
		onesDigit,
		tensDigit,
		hundredsDigit,
		placeValueNames,
		splitValues,
		splitSum,
		digits,
	};
	retval.fullHeadings = _.map(placeValueNames, pv => {
		return retval[pv];
	});

	return retval;
};

const getEstimationPair = function(randomGen, op) {
	const x1 = op === "*" ? randomGen.inRange(2, 8) : randomGen.inRange(4, 20);
	const delta1 =
		op === "*" ? randomGen.inRange(1, 6) : randomGen.inRange(2, 20);

	const isMultOrDiv = _.includes(["*", "/"], op);
	const tens = isMultOrDiv ? 10 : 100;

	let y1 = randomGen.fillInDigits(x1, tens, 1);
	let y2 = randomGen.fillInDigits(x1 + delta1, tens, 1);

	if (op === "/") {
		y1 = randomGen.fillInDigits(x1, tens, 1);
		y2 = randomGen.fillInDigits(x1 + delta1, tens * 25, 1);
		y2 = y1 * Math.ceil(y2 / y1);
	}

	let terms = [y2, y1];

	if (!_.includes(["-", "/"], op)) terms = randomGen.permuteAll(terms);

	let scaleCandidates = [0.1];
	if (op !== "-") scaleCandidates.push(10);
	if (op === "*") scaleCandidates = scaleCandidates.concat([1 / 3, 3]);
	if (op === "/") scaleCandidates = [0.1, 0.2, 0.3, 3, 5, 10];

	const scale = randomGen.sample(scaleCandidates);

	const mistakeValues = [y2, Math.floor(scale * y1)];
	let mistake1 = _.round(eval(mistakeValues.join(op)));

	const exact = _.round(eval(terms.join(op)));

	if (op === "-" && randomGen.randomFlag()) {
		// add some variance for subtraction problems
		mistake1 = _.round((exact * randomGen.inRange(400, 600)) / 1000);
	}

	if (mistake1 >= 10) {
		const newMistake1 = mistake1 - (mistake1 % 10) + (exact % 10);
		if (newMistake1 !== exact) mistake1 = newMistake1;
	}

	const responses = randomGen.permuteAll([mistake1, exact]);

	return [terms, responses];
};

const getDecimalMultiplicationEstimationPair = function(
	randomGen,
	difficultSmall,
	difficultBig
) {
	// Lower difficulty means less digits for the big and/or small decimals.
	const bigDigitRange = difficultBig ? 3 : 2;
	const smallDigitRange = difficultSmall ? 3 : 2;
	const bigDecimal = randomGen.getQuickCheckDecimal(true, bigDigitRange);
	const smallDecimal = randomGen.getQuickCheckDecimal(false, smallDigitRange);

	let terms = [bigDecimal.decimal, smallDecimal.decimal];
	terms = randomGen.permuteAll(terms);

	// We round each decimal to its leading digit.
	// For instance, the leading digit of 1298.45 is the 1, the coefficient of 10^3, so bigDecimal.leadingDigitExponent would be 3.
	// The leading digit of .003129 is the 3, the coefficient of 10^{-3}, so smallDecimal.leadingDigitExponent would be -3.
	const roundedBigDecimal = _.round(
		bigDecimal.decimal,
		-bigDecimal.leadingDigitExponent
	);
	const roundedSmallDecimal = _.round(
		smallDecimal.decimal,
		-smallDecimal.leadingDigitExponent
	);
	// To how many significant digits should we round the product?
	const roundedBigLeadingDigit =
		bigDecimal.leadingDigit + (bigDecimal.nextDigit > 5 ? 1 : 0);
	const roundedSmallLeadingDigit =
		smallDecimal.leadingDigit + (smallDecimal.nextDigit > 5 ? 1 : 0);
	const leadingDigitProduct = roundedBigLeadingDigit * roundedSmallLeadingDigit;
	const precisionProduct =
		(leadingDigitProduct % 10 !== 0 ? 0 : -1) -
		bigDecimal.leadingDigitExponent -
		smallDecimal.leadingDigitExponent;
	const answer = _.round(
		roundedBigDecimal * roundedSmallDecimal,
		precisionProduct
	);
	// Generate wrong answer by multiplying or dividing right answer by 10.
	// Make sure the scaled wrong answer isn't less than the small decimal or larger than the big decimal.
	const scaleCandidates = [0.1, 10];
	let scale;
	if (scaleCandidates[0] * answer <= roundedSmallDecimal) {
		scale = scaleCandidates[1];
	} else if (scaleCandidates[1] * answer >= roundedBigDecimal) {
		scale = scaleCandidates[0];
	} else {
		scale = randomGen.sample(scaleCandidates);
	}

	const wrongAnswer = _.round(
		scale * answer,
		precisionProduct + (scale === 10 ? -1 : 1)
	);

	const responses = randomGen.permuteAll([wrongAnswer, answer]);

	return [terms, responses, answer];
};

const getDivisionEstimationPair = function(
	randomGen,
	roundFirst,
	digit1,
	factor,
	tens
) {
	const getDirection = () => randomGen.sample([1, -1]);
	const y1 = randomGen.fillInDigits(digit1, tens[0], getDirection(), true);
	const y2 = roundFirst
		? factor * digit1 * tens[1]
		: randomGen.fillInDigits(factor * digit1, tens[1], getDirection(), true);

	const terms = [y2, y1];

	const correct = (factor * tens[1]) / tens[0];

	const customRound = x => {
		const exact = _.round(x) + "";
		let sigfigs = 1;
		if (_.startsWith(exact, "11") || _.startsWith(exact, "12")) {
			sigfigs = 2;
		}
		return _.round(x, -1 * (exact.length - sigfigs));
	};

	let scaleFactors = [];
	let currentScaleFactor = 1;

	_.each(_.range(4), () => {
		scaleFactors.push(currentScaleFactor);
		currentScaleFactor *= randomGen.sample([2.3, Math.sqrt(10)]);
	});

	const pos = _.sample([0, 1, 2]);
	scaleFactors = [scaleFactors[pos], scaleFactors[pos + 1]];

	// const ansIndexCandidates = [1, 2];

	// // So that we don't have answer candidates that are ridiculously out of range
	// if (correct >= 40) {
	//   ansIndexCandidates.push(3);
	// }

	// if (correct <= 100) {
	//   ansIndexCandidates.push(0);
	// }

	const ansIndex = randomGen.sample([0, 1]);

	const correctScaleFactor = scaleFactors[ansIndex];

	const responses = _.map(scaleFactors, scaleFactor =>
		customRound((correct * scaleFactor) / correctScaleFactor)
	);

	return [terms, responses];
};

const getSmallestOrLargestNums = function(randomGen, op) {
	const difficulty = randomGen.sample(["easy", "medium", "hard"]);

	const oneDigits = _.range(0, 10);
	const twoDigits = _.range(10, 100);
	const threeDigits = _.range(100, 1000);

	const ignoreLeadingPop = (ignoreLeading, array) => {
		let possibleAnswer;

		const used = [];
		do {
			possibleAnswer = randomGen.pop(array);
			used.push(possibleAnswer);
		} while (
			ignoreLeading &&
			ignoreLeading.indexOf(String(possibleAnswer)[0]) !== -1
		);
		used.pop();

		array.push(...used);

		return possibleAnswer;
	};

	let get1Digit = ignoreLeading => {
		return ignoreLeadingPop(ignoreLeading, oneDigits);
	};
	const get2Digit = ignoreLeading => {
		return ignoreLeadingPop(ignoreLeading, twoDigits);
	};
	let get3Digit = ignoreLeading => {
		return ignoreLeadingPop(ignoreLeading, threeDigits);
	};
	const get12Digit = ignoreLeading => {
		return (randomGen.randomFlag() ? get1Digit : get2Digit)(ignoreLeading);
	};

	// Just assume you're dealing with largest for easy/medium modes
	if (op === "smallest" && difficulty !== "hard") {
		const temp = get1Digit;
		get1Digit = get3Digit;
		get3Digit = temp;
	}

	const numbers = [];

	if (difficulty === "easy") {
		numbers.push(get3Digit());
		numbers.push(get12Digit());
		numbers.push(get12Digit());
		numbers.push(get12Digit());
	} else if (difficulty === "medium") {
		let temp;
		if (randomGen.randomFlag()) {
			numbers.push((temp = get3Digit()));
			numbers.push(get3Digit(String(temp)[0]));
			numbers.push(get12Digit());
			numbers.push(get12Digit());
		} else {
			numbers.push((temp = get2Digit()));
			numbers.push(get2Digit(String(temp)[0]));
			numbers.push(get1Digit());
			numbers.push(get1Digit());
		}
	} else if (difficulty === "hard") {
		let temp;
		if (op === "smallest") {
			numbers.push((temp = get2Digit()));
			numbers.push(get2Digit("0123456789".replace(String(temp)[0], "")));
			numbers.push(get3Digit());
			numbers.push(get3Digit());
		} else {
			numbers.push((temp = get3Digit()));
			numbers.push(get3Digit("0123456789".replace(String(temp)[0], "")));
			numbers.push(get2Digit());
			numbers.push(get2Digit());
		}
	}
	return [randomGen.permuteAll(numbers)];
};

const getSmallestOrLargestTwoDigitNums = function(randomGen, op) {
	const problemTypes = {
		sameTens: 0.1, // e.g. 42, 45, 48, 49
		sameOnes: 0.1, // e.g. 37, 57, 67, 87
		twoUniqueDigits: 0.1, // e.g. 22, 25, 52, 55
		monotonicDigits: 0.1, // e.g. 19, 38, 56, 82 (10s digit increase, 1s digit decrease)
		onesDigitZero: 0.1, // e.g. 21, 37, 48, 50 (largest/smallest has 1s digit zero)
		twoPairTens: 0.1, // e.g. 31, 39, 63, 67 (two pairs of 10s digits)
		uniqueDigits: 0.1, // 10s digits are unique, and 1s digits are unique
		repeatedTens: 0.3, // e.g. 29, 47, 55, 59 (two numbers have largest/smallest 10s digit)
	};
	const problemType = randomGen.sampleWeighted(problemTypes);
	const numbers = [];
	if (problemType === "sameTens") {
		const tensDigit = randomGen.inRange(1, 9);
		const onesDigits = randomGen.inRangeN(0, 10, 4);
		for (let i = 0; i < 4; i++) {
			numbers.push(10 * tensDigit + onesDigits[i]);
		}
	} else if (problemType === "sameOnes") {
		const onesDigit = randomGen.inRange(0, 9);
		const tensDigits = randomGen.inRangeN(1, 10, 4);
		for (let i = 0; i < 4; i++) {
			numbers.push(10 * tensDigits[i] + onesDigit);
		}
	} else if (problemType === "twoUniqueDigits") {
		const digits = randomGen.inRangeN(1, 10, 2);
		for (let i = 0; i < 2; i++) {
			for (let j = 0; j < 2; j++) {
				numbers.push(10 * digits[i] + digits[j]);
			}
		}
	} else if (problemType === "monotonicDigits") {
		const tensDigits = _.sortBy(randomGen.inRangeN(1, 10, 4));
		const onesDigits = _.sortBy(randomGen.inRangeN(0, 10, 4), x => -x);
		for (let i = 0; i < 4; i++) {
			numbers.push(10 * tensDigits[i] + onesDigits[i]);
		}
	} else if (problemType === "onesDigitZero") {
		const tensDigits =
			op === "largest"
				? _.sortBy(randomGen.inRangeN(1, 10, 4))
				: _.sortBy(randomGen.inRangeN(1, 10, 4), x => -x);
		for (let i = 0; i < 3; i++) {
			numbers.push(10 * tensDigits[i] + randomGen.inRange(1, 9));
		}
		numbers.push(10 * tensDigits[3]);
	} else if (problemType === "twoPairTens") {
		const tensDigits = randomGen.inRangeN(1, 10, 2);
		const onesDigits = _.sortBy(randomGen.inRangeN(0, 10, 4));
		numbers.push(10 * tensDigits[0] + onesDigits[0]);
		numbers.push(10 * tensDigits[0] + onesDigits[3]);
		numbers.push(10 * tensDigits[1] + onesDigits[1]);
		numbers.push(10 * tensDigits[1] + onesDigits[2]);
	} else if (problemType === "uniqueDigits") {
		const tensDigits = randomGen.inRangeN(1, 10, 4);
		const onesDigits = randomGen.inRangeN(0, 10, 4);
		for (let i = 0; i < 4; i++) {
			numbers.push(10 * tensDigits[i] + onesDigits[i]);
		}
	} else if (problemType === "repeatedTens") {
		const tensDigits =
			op === "largest"
				? _.sortBy(randomGen.inRangeN(1, 10, 3))
				: _.sortBy(randomGen.inRangeN(1, 10, 3), x => -x);
		const largestOnesDigits = randomGen.inRangeN(0, 10, 2);
		const otherOnesDigits = randomGen.inRangeN(0, 10, 2);
		numbers.push(10 * tensDigits[0] + otherOnesDigits[0]);
		numbers.push(10 * tensDigits[1] + otherOnesDigits[1]);
		numbers.push(10 * tensDigits[2] + largestOnesDigits[0]);
		numbers.push(10 * tensDigits[2] + largestOnesDigits[1]);
	}

	return [randomGen.permuteAll(numbers)];
};

const getDiceTrial = function(randomGen) {
	const diceWeights = {1: 0.04, 2: 0.08, 3: 0.16, 4: 0.24, 5: 0.24, 6: 0.24};
	const correctDie = randomGen.sampleWeighted(diceWeights);

	// Generate the list of 4 responses for the student to choose from.
	const responses = [correctDie];
	const responseWeights = {1: 0.25, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1};
	for (let i = 0; i < 3; i++) {
		// Randomly choose from the numbers that haven't already been picked.
		_.unset(responseWeights, responses[i]);
		responses.push(randomGen.sampleWeighted(responseWeights));
	}

	// Convert strings back to integers before returning.
	return [
		parseInt(correctDie),
		_.map(randomGen.permuteAll(responses), x => parseInt(x)),
	];
};

const getEasySubtractionPair = function(randomGen) {
	const problemTypes = {
		twoDigitNoCarry: 0.3, // e.g. 38-35
		twoDigitCarry: 0.2, // e.g. 33-29
		oneDigitNoCarry: 0.3, // e.g. 38-3
		oneDigitCarry: 0.2, // e.g. 33-4
	};
	const problemType = randomGen.sampleWeighted(problemTypes);
	// Chose the units digits of the two numbers.
	let unitsDigit1;
	let unitsDigit2;
	if (problemType === "twoDigitCarry" || problemType === "oneDigitCarry") {
		const unitsDigits = randomGen.inRangeN(0, 10, 2);
		unitsDigit1 = _.min(unitsDigits);
		unitsDigit2 = _.max(unitsDigits);
	} else if (problemType === "twoDigitNoCarry") {
		const unitsDigits = randomGen.inRangeN(0, 10, 2);
		unitsDigit1 = _.max(unitsDigits);
		unitsDigit2 = _.min(unitsDigits);
	} else {
		// In the oneDigitNoCarry problem, the units digits are allowed to be
		// equal (e.g. the problem 74-4).
		const digit1 = randomGen.inRange(0, 9);
		const digit2 = randomGen.inRange(0, 9);
		unitsDigit1 = _.max([digit1, digit2]);
		unitsDigit2 = _.min([digit1, digit2]);
	}
	// Chose the tens digits of the two numbers.
	const tensDigit1 = randomGen.inRange(2, 9);
	let tensDigit2;
	if (problemType === "twoDigitNoCarry") {
		tensDigit2 = tensDigit1;
	} else if (problemType === "twoDigitCarry") {
		tensDigit2 = tensDigit1 - 1;
	} else if (problemType === "oneDigitNoCarry" && unitsDigit2 === 0) {
		// If the subtraction problem would otherwise be X-0, set it to X-10.
		tensDigit2 = 1;
	} else {
		tensDigit2 = 0;
	}

	return [10 * tensDigit1 + unitsDigit1, 10 * tensDigit2 + unitsDigit2];
};

const getTenAdditionPair = function(randomGen) {
	const problemTypes = {
		tenPlusX: 0.15, // e.g. 10+7
		xPlusTen: 0.15, // e.g. 7+10
		multipleTenPlusX: 0.35, // e.g. 70+8
		xPlusMultipleTen: 0.35, // e.g. 8+70
	};
	const problemType = randomGen.sampleWeighted(problemTypes);
	let tens;
	let ones;
	if (problemType === "tenPlusX" || problemType === "xPlusTen") {
		tens = 10;
		ones = randomGen.inRange(0, 10);
	} else {
		tens = randomGen.inRange(2, 9) * 10;
		// Exclude 90+10 or 10+90.
		ones = tens === 90 ? randomGen.inRange(0, 9) : randomGen.inRange(0, 10);
	}
	if (problemType === "tenPlusX" || problemType === "multipleTenPlusX") {
		return [tens, ones];
	} else {
		return [ones, tens];
	}
};

const getCompareTwoDigitNumsPair = function(randomGen) {
	const problemTypes = {
		equal: 0.15, // e.g. 36 vs 36
		sameOnes: 0.1, // e.g. 36 vs 76
		sameTens: 0.1, // e.g. 36 vs 31
		closeNums: 0.19, // numbers are at most 4 apart with different tens digits
		swappedDigits: 0.1, // e.g. 36 vs 63
		largerTensAndOnes: 0.15, // one number has both tens and ones digit greater
		largerTensSmallerOnes: 0.21, // one number has larger tens and smaller ones digit
	};
	const problemType = randomGen.sampleWeighted(problemTypes);
	const numbers = [];
	if (problemType === "equal") {
		const num = randomGen.inRange(10, 99);
		numbers.push(num);
		numbers.push(num);
	} else if (problemType === "sameOnes") {
		const onesDigit = randomGen.inRange(0, 9);
		const tensDigits = randomGen.inRangeN(1, 10, 2);
		numbers.push(10 * tensDigits[0] + onesDigit);
		numbers.push(10 * tensDigits[1] + onesDigit);
	} else if (problemType === "sameTens") {
		const tensDigit = randomGen.inRange(1, 9);
		const onesDigits = randomGen.inRangeN(0, 10, 2);
		numbers.push(10 * tensDigit + onesDigits[0]);
		numbers.push(10 * tensDigit + onesDigits[1]);
	} else if (problemType === "closeNums") {
		const onesDigitPairs = [
			[6, 0],
			[7, 1],
			[8, 2],
			[9, 3],
			[7, 0],
			[8, 1],
			[9, 2],
			[8, 0],
			[9, 1],
			[9, 0],
		];
		const onesDigits = randomGen.sample(onesDigitPairs);
		const tensDigit = randomGen.inRange(1, 8);
		numbers.push(10 * tensDigit + onesDigits[0]);
		numbers.push(10 * tensDigit + 10 + onesDigits[1]);
	} else if (problemType === "swappedDigits") {
		const digits = randomGen.inRangeN(1, 10, 2);
		numbers.push(10 * digits[0] + digits[1]);
		numbers.push(10 * digits[1] + digits[0]);
	} else if (problemType === "largerTensAndOnes") {
		const tensDigits = _.sortBy(randomGen.inRangeN(1, 10, 2));
		const onesDigits = _.sortBy(randomGen.inRangeN(0, 10, 2));
		numbers.push(10 * tensDigits[0] + onesDigits[0]);
		numbers.push(10 * tensDigits[1] + onesDigits[1]);
	} else if (problemType === "largerTensSmallerOnes") {
		const tensDigits = _.sortBy(randomGen.inRangeN(1, 10, 2));
		const onesDigits = _.sortBy(randomGen.inRangeN(0, 10, 2));
		numbers.push(10 * tensDigits[0] + onesDigits[1]);
		numbers.push(10 * tensDigits[1] + onesDigits[0]);
	}
	return [randomGen.permuteAll(numbers)];
};

const replaceAllRegex = function(str, find, replaceFunc) {
	let nextStr = str;

	do {
		str = nextStr;
		nextStr = str.replace(find, replaceFunc);
	} while (str !== nextStr);

	return str;
};

const fillImpliedMultiplication = function(expr) {
	expr = replaceAllRegex(
		expr,
		/[0-9a-z\)\]][\(\[]/,
		match => match[0] + "*" + match[1]
	);
	expr = replaceAllRegex(
		expr,
		/[0-9a-z][\(\[]/,
		match => match[0] + "*" + match[1]
	);
	expr = replaceAllRegex(
		expr,
		/[\)\]][0-9a-z]/,
		match => match[0] + "*" + match[1]
	);

	expr = replaceAllRegex(
		expr,
		/[0-9][a-z]/,
		match => match[0] + "*" + match[1]
	);
	expr = replaceAllRegex(
		expr,
		/[a-z][a-z]/,
		match => match[0] + "*" + match[1]
	);

	return expr;
};

const removeMultiplication = function(expr) {
	expr = replaceAllRegex(expr, /\*[a-z]+/, match => match.slice(1));
	expr = replaceAllRegex(
		expr,
		/\*[0-9]+[a-z]*/,
		match => "(" + match.slice(1) + ")"
	);
	expr = expr.replace(/\*/g, "");
	return expr;
};

const simplifyByTerms = function(expr) {
	let terms = getSumTerms(expr);
	terms = _.map(terms, x => fillImpliedMultiplication(x));
	terms = _.map(terms, x => evalString(x));
	return joinSum(terms);
};

/// Functions related to Multiplication Algorithm
// Includes things that show up in 4-2 for example
// Most of this is taken from things developed for 4-2-9

/* Used to get the digits of a number.
   - Given
     -- n: an int (positive) (e.g. 123)
   - Returns
     -- array of ints (e.g. [1, 2, 3])
*/
const digitsOf = function(n) {
	return _.map(n.toString(), _.parseInt);
};

/* Used to get place values of number
   - Given
     -- n: an int (positive) (e.g. 142)
     -- ascending: bool, true if we want the ones digit first
   - Returns:
     -- array of ints (e.g. [2, 40, 100])
*/
const placeValues = function(n, ascending = true) {
	const places = [];
	const digitArr = digitsOf(n);
	_.reverse(digitArr);

	// We only care about nonzero contributions
	_.each(digitArr, function(digit, place) {
		if (digit !== 0) {
			places.push(digit * Math.pow(10, place));
		}
	});

	if (!ascending) {
		_.reverse(places);
	}
	return places;
};

/* Used to get place values of number.
   Modified version which includes zeroes in the list.
   - Given
     -- n: an int (positive) (e.g. 142)
     -- ascending: bool, true if we want the ones digit first
   - Returns:
     -- array of ints (e.g. [2, 40, 100])
*/
const placeValuesWithZeroes = function(n, ascending = true) {
	const places = [];
	const digitArr = digitsOf(n);
	_.reverse(digitArr);

	// We only care about nonzero contributions
	_.each(digitArr, function(digit, place) {
		places.push(digit * Math.pow(10, place));
	});

	if (!ascending) {
		_.reverse(places);
	}
	return places;
};

/* List out the computations of the partial products
 */
const computePartialProducts = function(m, n, includeZeroes = false) {
	const fstPlaces = includeZeroes ? placeValuesWithZeroes(m) : placeValues(m);
	const sndPlaces = includeZeroes ? placeValuesWithZeroes(n) : placeValues(n);

	const products = [];

	_.each(sndPlaces, function(nDig) {
		_.each(fstPlaces, function(mDig) {
			products.push(nDig * mDig);
		});
	});

	return products;
};

/* Used to write out partial products.
   - Given
     -- m: an int
     -- n: an int
     -- withAnswers: a bool, which if true indicates we should
                     include the answers for each product
   - Returns
     -- an array of strings
*/
const partialProductList = function(m, n, withAnswers = false) {
	const fstPlaces = placeValues(m);
	const sndPlaces = placeValues(n);

	const products = [];

	_.each(sndPlaces, function(nDig) {
		_.each(fstPlaces, function(mDig) {
			if (withAnswers) {
				products.push(
					`${nDig} * ${mDig} = ${NumberUtil.withCommas(nDig * mDig)}`
				);
			} else {
				products.push(`${nDig} * ${mDig}`);
			}
		});
	});

	return _.map(products, s => wrap(s, true));
};

/* Used to create an align environment that shows how the distributive
   property is used to generate some partial products.
   - Given
     -- m:         an int
     -- n:         an int
*/
const distributeProduct = function(m, n) {
	const fstPlaces = placeValues(m, false);
	const sndPlaces = placeValues(n, false);

	// Makes ure that ALT_LINE_LENGTH < MAX_PER_LINE
	const MAX_PER_LINE = 4;
	const ALT_LINE_LENGTH = 3;

	const products = _.map(unwrapArr(partialProductList(n, m)), s => `(${s})`);
	_.reverse(products);

	const alignLines = [];
	alignLines.push(`(${fstPlaces.join(" + ")}) * (${sndPlaces.join(" + ")})`);

	if (products.length > MAX_PER_LINE) {
		// This is a pretty hacky way to deal with long lines, so
		// if we think that we'll encounter many different sizes
		// of products, this should be rewritten.
		const productsHead = products.slice(0, ALT_LINE_LENGTH);
		const productsTail = products.slice(ALT_LINE_LENGTH);

		const longLine =
			productsHead.join(" + ") + `\\\\ \n &\\quad ` + productsTail.join(" + ");
		alignLines.push(longLine);
	} else {
		alignLines.push(products.join(" + "));
	}

	return makeAlign(`${m}*${n}`, "=", alignLines);
};

/* Used to create a longmult writeup.
 */
const makeLongMultEnv = function(m, n, withZeroes = false) {
	const beg = "\\longmult{";
	const med = "}{";
	const end = "}";

	const products = _.map(
		computePartialProducts(m, n, withZeroes),
		NumberUtil.withCommas
	);

	return `$${beg}${m};${n}${med}${products.join(
		";"
	)}${med}${NumberUtil.withCommas(m * n)}${end}$`;
};

/* Used to make previews for the long digit multiplication
   renderer. Includes $ signs.
*/
const makeLongMultPreview = function(m, n) {
	const beg = "\\begin{preview}[";
	const end = "]\\end{preview}";

	const products = computePartialProducts(m, n);
	const flattenedProductDigits = _.flatten(_.map(products, digitsOf));
	// Add the final answer of the overall product
	flattenedProductDigits.push(digitsOf(m * n));

	return `${beg}${flattenedProductDigits.join(",")}${end}`;
};

/* Used to setup a preview for a filled in Multiplication Table.
   - Given
     -- rows:    entries which appear in the rows (array of ints)
     -- cols:    entries which appear in the columns (array of ints)
     -- givenIx: the location which was already given (array of ints with length 0 or 2)
     -- isIncomplete: if set to true, leaves the bottom rightmost entry in the array blank
   - Returns
     -- a string which can be used in the solutionText field for creating a preview
*/
const makeMultTablePreview = function(
	rows,
	cols,
	givenIxs,
	isIncomplete = false
) {
	// Get an array of nulls
	const tSize = rows.length + 1;
	const ansArr = _.map(_.range(tSize), n => _.map(_.range(tSize), x => "null"));

	// Add all the answers to array
	_.each(_.range(1, tSize), function(ix) {
		_.each(_.range(1, tSize), function(jx) {
			ansArr[ix][jx] = rows[ix - 1] * cols[jx - 1];
		});
	});

	// In case an entry was already filled in earlier
	if (givenIxs.length !== 0) {
		ansArr[givenIxs[0] + 1][givenIxs[1] + 1] = "null";
	}

	if (isIncomplete) {
		ansArr[tSize - 1][tSize - 1] = "null";
	}

	// Place the array in a string
	return wrapPreview(ansArr);
};

/* Same as makeMultTablePreview, except this only fills in the top leftmost entry.
   - Given
     -- rows:    entries which appear in the rows (array of ints)
     -- cols:    entries which appear in the columns (array of ints)
   - Returns
     -- a string which can be used in the solutionText field for creating a preview
*/
const weakMultTablePreview = function(rows, cols, givenIxs) {
	// Get an array of nulls
	const tSize = rows.length + 1;
	const ansArr = _.map(_.range(tSize), n => _.map(_.range(tSize), x => "null"));

	ansArr[1][1] = rows[0] * cols[0];

	return wrapPreview(ansArr);
};

/// Functions for Setting up Environments

/* Helper function to make a generic tabularMath environment.
   - Given
     -- specs: a string specifying the orientation of columns
     -- div: the divider used between rows (single string)
     -- lines: an array of strings, each entry corresponding to
               a row of the table, in order
   - Returns
     -- a string tabularmath environment
*/
const makeTable = function(specs, div, lines) {
	const tableL = [];

	const beg = `\\begin{tabularmath}{${specs}}`;
	const end = `\\end{tabularmath}`;

	tableL.push(beg);
	_.each(lines, function(line, idx) {
		tableL.push(div);
		tableL.push(line);
	});
	tableL.push(div);
	tableL.push(end);

	const finalTable = tableL.join("\n");
	return finalTable;
};

/* Helper function for setting up specific align environment.
   - Given
     -- start: a string s
     -- div: a seperator d appearing after each &
     -- lines: the array of strings, each entry corresponding
               to a line in the align environment
   - Returns
     -- a string align environment like
         s &d line1
           &d line2
           ...
           &d lineN
*/
const makeAlign = function(start, div, lines) {
	const alignList = [];

	const beg = `\\begin{align}`;
	const end = `\\end{align}`;

	let modLine;
	let appendage;
	const size = lines.length;

	alignList.push(beg);

	_.each(lines, function(line, idx) {
		// Setup beginning and end lines slightly differently
		modLine = idx === 0 ? `${start}` : "";
		modLine += `&${div}`;
		modLine += line;

		appendage = idx === size - 1 ? "." : `\\\\`;
		modLine += appendage;

		alignList.push(modLine);
	});

	alignList.push(end);

	const finalAlign = alignList.join("\n");

	return finalAlign;
};

/* Helper function for wrapping environments in a blocklist.
   - Given
     -- blockLines: an array of strings, with each entry corresponding
                    to an individual block
   - Returns
     -- a string corresponding to the blocklist containing the given
        environments
*/
const makeBlockList = function(blockLines) {
	const beg = "\\blocklist{\n";
	const end = "\n}";

	return `${beg}${blockLines.join("\n")}${end}`;
};

/// Making Sums, Products, and Exponents

/* Used to write repeated sum of an integer
   - Given
     -- addend: a string
     -- factor: an int

   - Returns
     -- the sum of addend, factor times (not wrapped in $ signs)
*/
const makeSum = function(addend, factor) {
	const plusSign = "+";
	const rawSum = _.repeat(addend + plusSign, factor - 1) + addend;

	return rawSum;
};

/* Helper function used to write repeated product of an integer
   - Given
     -- base: a string
     -- exp: an int

   - Returns
     -- the product of base, exp times (not wrapped in $ signs)
*/
const makeProduct = function(base, exp) {
	const ast = "*";
	const rawMult = _.repeat(base + ast, exp - 1) + base;

	return rawMult;
};

/* Helper function used to write a single exponentiation.
   - Given
     -- base: a string
     -- exp: a string

   - Returns
     -- base^exp as a string (not wrapped in $ signs)
*/
const makeExp = function(base, exp) {
	return `${base}^{${exp}}`;
};

/// General Formatting

/* Helper function to wrap in math environment.
   - Given
     -- mth: the text to be wrapped
     -- small: a bool, with false corresponding to $$ ... $$ instead of $ ... $
*/
const wrap = function(mth, small = true) {
	const wrapping = small ? "$" : "$$";
	return wrapping + mth + wrapping;
};

/* Helper function for unwrapArr
 */
const unwrap = function(s) {
	return s.slice(1, -1);
};

/* Used in distributeProduct helper function (higher up in the Multiplication
   Algorithm section).
   - Given
     -- arr: an array of strings, where each element begins and ends
             with a $ sign
   - Returns
     -- an array that is the same as the original, each element does not
        has a single $ sign omitted from the beginning and omitted from the end
*/
const unwrapArr = function(arr) {
	return _.map(arr, unwrap);
};

/* Used to write in English the number of numbers we have.
   - Given
     -- base: some string b
     -- exp: some int n
     -- interrupt: some string msg (needs a space after)
   - Returns
     -- "n msg b's" as a string
*/
const spellOut = function(base, exp, interrupt) {
	const end = exp === 1 ? "" : "'s";
	return `${WordUtil.toWord(exp)} ${interrupt}${base}${end}`;
};

/* Used to wrap an array of arrays in a preview environment,
   while keeping the brackets safe. Used specifically for setting
   up some Multiplication Table thing.
*/
const wrapPreview = function(arr) {
	const beg = "\\begin{preview}";
	const end = "\\end{preview}";

	const arrString = _.map(arr, xs => `[${xs.toString()}]`);

	return `${beg}[${arrString}]${end}`;
};

/// Combining arrays

/* Used for interleaving two arrays.
   - Given
     -- outerArr: ["a", "b", "c"]
     -- innerArr: ["+", "-"]
     -- div: (some str) " "
   - Returns
     -- str: "a + b - c"
   - Assumes
     -- innerArr.length + 1 = outerArr.length
*/
const weave = function(outerArr, innerArr, div = " ") {
	const wovenArr = [];
	_.each(innerArr, function(elem, idx) {
		wovenArr.push(outerArr[idx]);
		wovenArr.push(elem);
	});
	wovenArr.push(outerArr[innerArr.length]);

	return wovenArr.join(div);
};

const simplStepsExplicit = function(expr) {
	const cleanedExpr = expr.replace(/\[|\]/g, ""); //remove brackets
	const steps = [cleanedExpr];
	if (expr.includes("[")) {
		const bracketMatcher = /\[([^]+)\]/; //extracts text between brackets
		const bracketExec = bracketMatcher.exec(expr);
		const innerExpr = bracketExec[1];

		const innerEval = evalString(innerExpr);
		const nextExpr = expr.replace(bracketExec[0], innerEval);
		steps.push(nextExpr);
	}

	steps.push(evalString(cleanedExpr));

	return steps;
};

const simplStepsNatural = function(expr) {
	const cleanedExpr = expr.replace(/\[|\]/g, ""); //remove brackets
	let steps = [cleanedExpr];

	const sumTerms = getSumTerms(cleanedExpr);
	if (sumTerms.length > 1) {
		const simpTerms = _.map(sumTerms, x => evalString(x));
		const simpExpr = joinSum(simpTerms);
		steps.push(simpExpr);
	}

	steps.push(evalString(cleanedExpr));
	steps = _.uniq(steps);

	return steps;
};

const simplStepsVar = function(expr, varMap, useNaturalSimplifcation) {
	const signParenMap = {};

	_.each(varMap, (value, x) => {
		signParenMap[x] = value < 0 ? `(${value})` : value;
	});

	const simpFunc = useNaturalSimplifcation
		? simplStepsNatural
		: simplStepsExplicit;

	const expr2 = fillImpliedMultiplication(expr);
	const intermediateStep1 = substitute(expr2, signParenMap);
	const simpSteps = simpFunc(intermediateStep1);
	let steps = _.concat([expr2], simpSteps);

	if (useNaturalSimplifcation) {
		steps = _.map(steps, x => removeMultiplication(x + ""));
	} else {
		steps = _.map(steps, x => cleanExpr(x + ""));
	}

	return steps;
};

const simplSumTerms = function(expr) {
	const sumTerms = getSumTerms(expr);
	const terms = _.map(sumTerms, x => simplTerm(x));
	return joinSum(terms);
};

const groupTerms = function(sumTerms, expr) {
	const grouped = {};
	_.each(sumTerms, term => {
		const variable = getVariables(term)[0] || "";
		if (!grouped[variable]) grouped[variable] = [];
		const cleanedExpr = cleanExpr(removeVariables(term), true);

		if (cleanedExpr === "-") term = -1;
		else if (cleanedExpr === "") term = 1;
		else term = parseInt(cleanedExpr, 10);

		grouped[variable].push(term);
	});

	return grouped;
};

const coeffStringToValue = function(str) {
	if (str === "") return 1;
	if (str === "-") return -1;

	return parseInt(str, 10);
};

const coeffValueToString = function(value) {
	if (value === 1) return "";
	if (value === -1) return "-";

	return value + "";
};

const simplLikeTerms = function(expr, skipSimpleSteps) {
	const steps = [expr];

	expr = simplSumTerms(expr);

	steps.push(expr);

	let variables = getVariables(expr);
	variables = _.sortBy(variables);
	const keys = variables.concat([""]);

	const sumTerms = getSumTerms(expr);
	const grouped = groupTerms(sumTerms, expr);

	let termLists = _.map(keys, key =>
		_.map(grouped[key], x => coeffValueToString(x) + key)
	);
	termLists = _.filter(termLists, x => x.length > 0);

	const middle = _.map(termLists, (terms, i) => {
		const sum = joinSum(terms, true, skipSimpleSteps);
		return terms.length === 1 && (skipSimpleSteps || _.startsWith(sum, "("))
			? sum
			: `(${sum})`;
	});

	const ansTerms = [];
	const preAnsTerms = [];
	_.each(keys, key => {
		const group = grouped[key];
		if (!group) return;
		const sum = _.sum(group);
		preAnsTerms.push(coeffValueToString(sum) + key);
		if (sum !== 0) {
			ansTerms.push(coeffValueToString(sum) + key);
		}
	});

	const ans = joinSum(ansTerms);

	if (!skipSimpleSteps) steps.push(joinSum(sumTerms, true));
	if (termLists.length > 1) steps.push(joinSum(middle));
	steps.push(joinSum(preAnsTerms));
	steps.push(ans);

	return {
		variables,
		ans,
		ansTerms,
		termLists,
		steps,
	};
};

const distributeTerm = function(givenExpr) {
	let expr = fillImpliedMultiplication(givenExpr);
	const sumTermLists = _.map(expr.split("*"), x => getSumTerms(x));

	let products = [[]];
	const factors = [];

	_.each(sumTermLists, sumTerms => {
		const nextProducts = [];

		if (sumTerms.length === 1) {
			factors.push(sumTerms[0]);
		}

		_.each(products, product => {
			_.each(sumTerms, term => {
				nextProducts.push(product.concat(term));
			});
		});

		products = nextProducts;
	});

	products = _.map(products, x => removeMultiplication(x.join("*")));
	expr = joinSum(products);

	return {
		expr,
		factors,
	};
};

const distribute = function(givenExpr) {
	const sumTerms = getSumTerms(givenExpr);
	let factors = [];
	const terms = [];
	_.each(sumTerms, t => {
		const termInfo = distributeTerm(t);
		const term = termInfo.expr;
		terms.push(term);

		if (t !== term) {
			factors.push(termInfo.factors);
		}
	});

	factors = _.flatten(factors);
	const expr = joinSum(terms);

	return {
		expr,
		factors,
	};
};

const getCoeffs = function(expr) {
	expr = removeVariables(expr);
	expr = fillImpliedMultiplication(expr);
	expr = cleanExpr(expr, true);
	const coeffs = _.map(expr.split("*"), x => coeffStringToValue(x));
	return coeffs;
};

const splitByCoeffs = function(expr) {
	const vars = getVariables(expr);
	const coeffs = getCoeffs(expr);

	let varExpr = vars.join("");
	if (vars.length > 1) varExpr = "(" + varExpr + ")";

	return "(" + coeffs.join("*") + ")" + varExpr;
};

const simplTerm = function(expr) {
	const vars = getVariables(expr, true);
	const coeffs = getCoeffs(expr);
	const coeffValue = calcProduct(coeffs);
	if (coeffValue === 0) return "";
	let simpExpr = coeffValueToString(coeffValue) + vars.join("");
	if (vars.length <= 1 && coeffs.length <= 1 && isParenEnclosed(expr)) {
		simpExpr = "(" + simpExpr + ")";
	}
	return simpExpr;
};

const cleanExpr = function(expr, parenAndBrackets) {
	const bracketsRegex = parenAndBrackets ? /\[|\]|\(|\)/g : /\[|\]/g;
	expr = expr.replace(bracketsRegex, "");
	if (!parenAndBrackets) expr = cleanCdot(expr);
	return expr;
};

const cleanCdot = function(expr) {
	return replaceAll(expr, "*", " \\cdot ");
};

const parseCoordinates = function(coordStr) {
	return coordStr
		.slice(1, -1)
		.split(",")
		.map(Number);
};

const regularDiagramColors = ["orange", "green", "blue", "purple", "pink"];
const lightDiagramColors = [
	"lightOrange",
	"lightGreen",
	"lightBlue",
	"lightPurple",
	"lightPink",
];

module.exports = {
	getMinIndex,
	getMaxIndex,

	gcd,
	lcm,

	calcProduct,

	fmt,
	fmtSide,
	genArray,
	mult,
	generalFrac,
	toMixed,
	toImproper,
	simplifyFraction,
	evalGeneral,
	plus,
	plusAll,
	align,
	diagram,
	preview,
	table,
	hasPosSign,
	repAdd,
	basicSumEq,
	fmtEqLines,
	formatEquationLines,
	constructFactorExprLines,
	zippedFactorLines,
	constructAdvancedFactorLines,
	twoNumberProductLines,
	twoNumberProductLinesDirection,

	sumNumbers,
	sumElements,
	sumZipped,
	parseZippedSum,

	fiveSquareLines,
	arrayDirection,
	sqDecomp,
	knownSq,
	neighboringSquareLines,
	formatNSLines,
	genFactorExpr,
	getBreakdown,
	genDistribute,
	multCloseLines,
	boxedMult,

	getSortOrder,

	countOccurrences,
	replaceAll,
	substitute,
	variableEquations,

	prepForOp,
	verbForOp,
	opToResultWord,
	explanationForOp,
	explanationForSolution,

	getSumTerms,
	joinSum,
	flipSubtraction,
	getSimplifyingSteps,
	solveEquation,
	simplifyExpr,
	verboseSolve,
	verboseRepeatVariableSolve,
	verboseSimplify,
	removeVariables,
	getVariables,
	getAllVariables,
	divisionInfo,

	arrowToFraction,
	labelArrowToFraction,
	manyArrowsToFraction,
	standardNumberLine,
	shadeToFraction,

	round,
	roundToMultiple,
	roundDefault,
	roundDivision,
	getBoundingMultiples,
	getMultipleForRounding,

	getEstimationChoiceInfo,
	evalGeneralOp,
	getEstimateInfo,
	getDuplicateEstimateProblemInfo,

	getMode,

	toSetSeed,

	preprocessSubstitutions,
	addNameSubstitutions,
	addArraySubstitutions,

	avoidUsedPairsPredicate,
	filterMany,
	permuteTwoAvoidPairs,
	getAvoidancePredicates,

	sampleEquivSums,
	sampleEquivProducts,

	regularDiagramColors,
	lightDiagramColors,

	getShapeTemplateForFraction,

	getFactorDetails,
	getEstimationPair,
	getDecimalMultiplicationEstimationPair,
	getDivisionEstimationPair,
	getSmallestOrLargestNums,
	getSmallestOrLargestTwoDigitNums,
	getDiceTrial,
	getEasySubtractionPair,
	getTenAdditionPair,
	getCompareTwoDigitNumsPair,

	fillImpliedMultiplication,
	removeMultiplication,
	simplifyByTerms,

	evalString,

	digitsOf,
	placeValues,
	placeValuesWithZeroes,
	computePartialProducts,
	partialProductList,
	distributeProduct,
	makeLongMultEnv,
	makeLongMultPreview,
	makeMultTablePreview,
	weakMultTablePreview,

	makeTable,
	makeAlign,
	makeBlockList,

	makeSum,
	makeProduct,
	makeExp,

	wrap,
	unwrap,
	unwrapArr,

	spellOut,

	wrapPreview,

	weave,

	simplStepsExplicit,
	simplStepsVar,
	simplTerm,
	simplSumTerms,
	simplLikeTerms,
	distribute,
	splitByCoeffs,
	cleanExpr,
	parseCoordinates,
};
